<template>
  <v-card elevation="0" outlined class="d-flex flex-column h-100" min-height="350">
    <v-card-title class="pb-0">
      <v-icon color="secondary" large class="mr-2">event</v-icon>
      <div class="d-flex flex-column">
        <span class="primary--text monospace-font body-1 font-weight-medium">{{ $t("scoutingCalendar.tasks.myTasks") }}</span>
        <span class="subheader-font body-2">{{ $t("scoutingCalendar.tasks.widgetSubtitle") }}</span>
      </div>
    </v-card-title>
    <v-card-text class="d-flex flex-column pb-1 flex-grow-1">
      <!--Tabs-->
      <v-tabs v-model="tabModel" class="my-0" grow color="secondary darken-1">
        <v-tab>{{$t('general.today')}}</v-tab>
        <v-tab>{{$t('scoutingTasksBoard.period.week')}}</v-tab>
      </v-tabs>
      <!--Items-->
      <v-tabs-items v-model="tabModel" style="background:transparent" class="overflow-auto h-100">
        <!--Today-->
        <v-tab-item class="transparent">
          <v-data-table dense disable-items-per-page multi-sort
            :headers="headers" 
            :items="todayTasks"
            :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [5]}"
            sort-by="start" :sort-desc="true"
            :no-data-text="$t('scoutingCalendar.tasks.noData')"
            class="pa-0"
          >
            <!--Name-->
            <template v-slot:item.name="{ item }">
              <router-link class="py-2 d-flex align-center justify-space-between nav-link" :to="`/tasks-board?id=${item.id}`">
                <span class="text-ellipsis w-90 body-1">
                  <v-tooltip top color="lightSecondary lighten-4" v-if="item.typeId && item.typeId !== 3" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-on="on" v-bind="attrs" small>{{ getEventTypeIcon(item) }}</v-icon>
                    </template>
                    <span class="black--text body-1">{{ getEventTypeText(item) }}</span>
                  </v-tooltip>
                  {{ item.name }}
                </span>
              </router-link>
            </template>
            <!--State-->
            <template v-slot:item.boardStateId="{ item }">
              <v-chip small :color="getEventStateColor(item)" class="white--text monospace-font">{{ getEventStateText(item) }}</v-chip>
            </template>
            <!--Assigned users-->
            <template v-slot:item.userAssignedIds="{ item }">
              <v-tooltip top color="lightSecondary lighten-4" v-if="item.userAssignedIds" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-on="on" v-bind="attrs" small>person</v-icon>
                </template>
                <span class="black--text body-1">{{ item.userAssignedNames }}</span>
              </v-tooltip>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-tab-item>
        <!--This week-->
        <v-tab-item class="transparent">
          <v-data-table dense disable-items-per-page multi-sort
            :headers="headers" 
            :items="thisWeekTasks"
            :footer-props="{ itemsPerPageOptions: $vuetify.breakpoint.smAndDown ? [1] : [5]}"
            sort-by="start" :sort-desc="true"
            :no-data-text="$t('scoutingCalendar.tasks.noData')"
            class="pa-0"
          >
            <!--Name-->
            <template v-slot:item.name="{ item }">
              <router-link class="py-2 d-flex align-center justify-space-between nav-link" :to="`/tasks-board?id=${item.id}`">
                <span class="text-ellipsis w-90">
                  <v-tooltip top color="blue-grey lighten-5" v-if="item.typeId && item.typeId !== 3" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-on="on" v-bind="attrs" x-small>{{ getEventTypeIcon(item) }}</v-icon>
                    </template>
                    <span class="black--text body-1">{{ getEventTypeText(item) }}</span>
                  </v-tooltip>
                  {{ item.name }}
                </span>
              </router-link>
            </template>
            <!--State-->
            <template v-slot:item.boardStateId="{ item }">
              <v-chip x-small :color="getEventStateColor(item)" class="white--text">{{ getEventStateText(item) }}</v-chip>
            </template>
            <!--Assigned users-->
            <template v-slot:item.userAssignedIds="{ item }">
              <v-tooltip top color="blue-grey lighten-5" v-if="item.userAssignedIds" max-width="300">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon v-on="on" v-bind="attrs" x-small>person</v-icon>
                </template>
                <span class="black--text body-1">{{ item.userAssignedNames }}</span>
              </v-tooltip>
              <span v-else>-</span>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
      <v-divider class="mt-1"></v-divider>
    </v-card-text>
    <v-card-actions class="justify-end flex-shrink-0">
      <v-btn color="secondary" class="elevation-0 monospace-font white--text px-5" rounded small :to="`/tasks-board`">{{$t('scoutingTasksBoard.goToBoard')}}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment';
import CalendarService from '../../../services/calendar.service';

export default {
  name: "ScoutingTasksWidget",
  data() {
    return {
      todayTasks: [],
      thisWeekTasks: [],
      tabModel: 0,
      loading: false,
      today: moment().format('YYYY-MM-DD'),
      tomorrow: moment().add(1, 'days').format('YYYY-MM-DD'),
      dateFrom: moment().startOf('week').format('YYYY-MM-DD'),
      dateTo: moment().endOf('week').format('YYYY-MM-DD'),
      headers: [
        {
          text: this.$t("playerDetail.name"),
          value: "name",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("general.state"),
          value: "boardStateId",
          align: "start",
          sortable: false,
        },
        {
          text: this.$t("scoutingCalendar.tasks.assignedTo"),
          value: "userAssignedIds",
          align: "center",
          sortable: false,
        },
      ]
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
  },
  async created() {
    this.loading = true;
    await this.getTodayTasks();
    await this.getThisWeekTasks();
    this.loading = false;
  },
  methods: {
    async getTodayTasks() {
      const todayData = await CalendarService.getCalendarEventsByUserIdAndDates(this.currentUser.id, this.today, this.today);
      this.todayTasks = todayData.data;
      const todayTasksIds =  this.todayTasks.map(t => t.id);
      //Map users assigned to each task
      if (this.todayTasks.length) {
        const assignedUsersData = await CalendarService.getUsersAssignedByEventCalendarIds(todayTasksIds);
        this.todayTasks = this.formatTasks(this.todayTasks, assignedUsersData);
      };
    },
    async getThisWeekTasks() {
      const thisWeekData = await CalendarService.getCalendarEventsByUserIdAndDates(this.currentUser.id, this.dateFrom, this.dateTo);
      this.thisWeekTasks = thisWeekData.data;
      const weekTasksIds =  this.thisWeekTasks.map(t => t.id);
      //Map users assigned to each task
      if (this.thisWeekTasks.length) {
        const assignedUsersData = await CalendarService.getUsersAssignedByEventCalendarIds(weekTasksIds);
        this.thisWeekTasks = this.formatTasks(this.thisWeekTasks, assignedUsersData);
      };
    },
    getEventStateColor(e) {
      switch (e.boardStateId) {
        case 1:
          return 'red';
        case 2:
          return 'amber';
        case 3:
          return 'green';
        default:
          return 'primary';
      };
    },
    getEventStateText(e) {
      switch (e.boardStateId) {
        case 1:
          return this.$t('scoutingCalendar.tasks.states.toDo');
        case 2:
          return this.$t('scoutingCalendar.tasks.states.inProgress');
        case 3:
          return this.$t('scoutingCalendar.tasks.states.done');
        default:
          return this.$t('scoutingCalendar.tasks.states.toDo');
      };
    },
    getEventTypeIcon(e) {
      switch (e.typeId) {
        case 1:
          return 'directions_run';
        case 2:
          return 'shield';
        case 3:
          return 'event';
        default:
          return 'event';
      };
    },
    getEventTypeText(e) {
      switch (e.typeId) {
        case 1:
          return this.$t('scoutingCalendar.tasks.type.player');
        case 2:
          return this.$t('scoutingCalendar.tasks.type.team');
        case 3:
          return this.$t('scoutingCalendar.tasks.type.other');
        default:
          return this.$t('scoutingCalendar.tasks.type.other');
      };
    },
    formatTasks(tasksArr, assignedUsersData) {
      return tasksArr.map(e => {
        e.assignedUsers = assignedUsersData.data.length ? assignedUsersData.data.filter(u => u.eventId === e.id) : [];
        e.userAssignedIds = e.assignedUsers.length ? e.assignedUsers.map(u => u.id) : null;
        e.userAssignedNames = e.assignedUsers.length ? e.assignedUsers.map(u => `${u.firstName} ${u.lastName}`).join(", ") : null;
        return e;
      });
    }
  }
};
</script>


// WEBPACK FOOTER //
// src/views/dashboard/widgets/ScoutingTasksWidget.vue